import React from 'react';
import { Target, Shield, Award, Users, TrendingUp, Leaf, Lightbulb } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission and Vision',
      description:
        'Our mission is to provide world‑class engineering that enhances performance, protects people, and supports responsible energy development. Our vision is to be a global benchmark for excellence, innovation, and sustainability in the oil and gas sector.'
    },
    {
      icon: Shield,
      title: 'Safety First',
      description:
        'Safety is not just a priority—it\'s our culture. We maintain the highest safety standards in every operation.'
    },
    {
      icon: Award,
      title: 'Quality Excellence',
      description:
        'We are committed to delivering excellence through rigorous quality control and continuous improvement.'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description:
        'Sustainability in the oil and gas engineering industry means delivering energy solutions that reduce environmental impact while improving efficiency and safety. Through innovation and disciplined engineering, we help build a more sustainable, resilient, and future‑ready energy sector.'
    },
    {
      icon: Lightbulb,
      title: 'Value Engineering',
      description:
        'Our value engineering approach focuses on optimizing designs, reducing lifecycle costs, and enhancing system performance through rigorous analysis and advanced engineering tools. By integrating efficiency, safety, and reliability into every decision, we deliver high value solutions that strengthen project outcomes across the oil and gas sector.'
    },
    {
      icon: Users,
      title: 'Teamwork',
      description:
        'Our success is built on collaboration, mutual respect, and the collective expertise of our talented team.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description:
        'We embrace innovation and leverage cutting-edge technologies to deliver superior solutions.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://images.pexels.com/photos/3831159/pexels-photo-3831159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Total Vision</h1>
          <p className="text-xl max-w-2xl mx-auto px-4">
            Oil and Gas Fields Services and Consultancy LLC - SPC
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Total Vision Oil and Gas Fields Services and Consultancy LLC - SPC was established with a clear vision:
                  to provide world-class engineering and project management services to the oil and gas industry. Based
                  in Abu Dhabi, UAE, we have grown to become a trusted partner for major operators across the Middle
                  East and beyond.
                </p>
                <p>
                  Our journey has been marked by a commitment to excellence, safety, and innovation. We've successfully
                  delivered numerous complex projects, from oil field developments to pipeline infrastructure, earning
                  the trust of industry leaders like BP, CNPC, Total, and Petronas.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/15961091/pexels-photo-15961091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Oil & Gas Infrastructure"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About TOTAL VISION */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 text-center">
              About <span className="text-red-600">TOTAL VISION</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-12" />
            
            <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
              <p>
                At <strong>TOTAL VISION Oil and Gas Fields Services and Consultancy LLC SPC</strong>, teamwork is more than a principle — it is the driving force behind everything we do. We recognize that no single individual holds all the answers, which is why we approach every project as a unified team, combining our collective expertise to deliver solutions that are accurate, efficient, and fully aligned with client expectations.
              </p>
              
              <p>
                Our in‑house <strong>Cold Eye Review</strong> process ensures that every design, concept, and deliverable is thoroughly evaluated from multiple professional perspectives. This not only saves our clients significant man‑hours but also provides the assurance that every possible scenario has been considered before the final product is delivered.
              </p>
              
              <p>
                Our team is our greatest asset — diverse, experienced, and committed to continuous improvement. We believe that every professional brings valuable knowledge, and we channel this strength into structured enrichment and mentoring pathways that follow a clear, predefined route map. The result is simple: empowered professionals, stronger project outcomes, and a seamless one‑stop solution for our clients.
              </p>
              
              <div className="mt-8 pt-8 border-t-2 border-red-600">
                <p className="text-xl font-semibold text-slate-900 text-center">
                  At TOTAL VISION, we don't just deliver services.
                </p>
                <p className="text-2xl font-bold text-red-600 text-center mt-2">
                  We deliver confidence, clarity, and complete solutions — together.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our <span className="text-blue-600">Core Values</span>
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6" />
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">20+</div>
              <div className="text-red-100">Years of Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">50+</div>
              <div className="text-red-100">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">15+</div>
              <div className="text-red-100">Countries Served</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100+</div>
              <div className="text-red-100">Expert Professionals</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;